import { API } from "aws-amplify";
import { NolaApiInitializer } from "@/initializer";
import { NolaPfContestProfile, UpdateNolaPfContestProfileInput } from "../models/nolaPfContest";

const API_NAME = "NolaPfContestAPI";
const PROFILE_PATH = "/profile";

type ProfileResponse = Omit<Partial<NolaPfContestProfile>, "gender"> & {
  email?: string;
  gender?: number | null;
};

interface INolaPfContestClient {
  fetchProfile: () => Promise<NolaPfContestProfile | null>;
  updateProfile: (payload: UpdateNolaPfContestProfileInput) => Promise<NolaPfContestProfile>;
}

function isNotFoundError(error: unknown): boolean {
  if (!error || typeof error !== "object" || !("response" in error)) {
    return false;
  }
  const { response } = error as { response?: { status?: number } };
  return response?.status === 404;
}

const UNSELECTED_GENDER = 0;

/** 0 / null / 未返却は未選択として扱う。 */
function toGender(gender: unknown): number {
  if (gender === UNSELECTED_GENDER || gender === null || gender === undefined) {
    return UNSELECTED_GENDER;
  }
  return typeof gender === "number" ? gender : UNSELECTED_GENDER;
}

/** スキーマの email は保持しない。 */
function toProfile(response: ProfileResponse): NolaPfContestProfile {
  return {
    userId: response.userId ?? "",
    name: response.name ?? "",
    nameKana: response.nameKana ?? "",
    pennames: Array.isArray(response.pennames) ? response.pennames.filter((name) => !!name) : [],
    phone: response.phone ?? "",
    gender: toGender(response.gender),
    birthDate: response.birthDate ?? "",
    historyOfAwards: response.historyOfAwards ?? "",
    createdAt: response.createdAt ?? "",
    updatedAt: response.updatedAt ?? "",
  };
}

/** 更新ボディはスキーマの更新項目だけにする。email は送らない。 */
function toUpdateBody(payload: UpdateNolaPfContestProfileInput): UpdateNolaPfContestProfileInput {
  const body: UpdateNolaPfContestProfileInput = {};
  if (payload.name !== undefined) body.name = payload.name;
  if (payload.nameKana !== undefined) body.nameKana = payload.nameKana;
  if (payload.pennames !== undefined) body.pennames = payload.pennames;
  if (payload.phone !== undefined) body.phone = payload.phone;
  if (payload.gender !== undefined) body.gender = payload.gender;
  if (payload.birthDate !== undefined) body.birthDate = payload.birthDate;
  if (payload.historyOfAwards !== undefined) body.historyOfAwards = payload.historyOfAwards;
  return body;
}

export class NolaPfContestClient implements INolaPfContestClient {
  /** プロフィールが無い（404）場合は null。 */
  async fetchProfile(): Promise<NolaPfContestProfile | null> {
    NolaApiInitializer.init();

    try {
      const result = (await API.get(API_NAME, PROFILE_PATH, {})) as ProfileResponse;
      return toProfile(result);
    } catch (error) {
      if (isNotFoundError(error)) return null;
      throw error;
    }
  }

  async updateProfile(payload: UpdateNolaPfContestProfileInput): Promise<NolaPfContestProfile> {
    NolaApiInitializer.init();

    const result = (await API.patch(API_NAME, PROFILE_PATH, {
      body: toUpdateBody(payload),
    })) as ProfileResponse;
    return toProfile(result);
  }
}
